import { useQuery, useMutation } from '@tanstack/react-query'
import { useAuthState, useAxios } from 'packages/core'
import appConfig from 'config'
import useUserManager from 'packages/core/auth/useUserManager'

export const CURRENT_PERSON_KEY = 'CURRENT_PERSON'
export const PERSON_KEY = 'PERSON'
export const MASTER_PASSWORD_PUBLIC_KEY = 'MasterPasswordPublicKey'
export const USER_INFO_KEY = 'USER_INFO'

export const useCurrentPerson = () => {
    const callApi = useAxios()
    return useQuery(
        [CURRENT_PERSON_KEY],
        () =>
            callApi({
                method: 'GET',
                url: '/api/people',
            }).then((data) => data.data),
        {
            staleTime: Infinity,
        },
    )
}
export const usePerson = (personId, enabled) => {
    const callApi = useAxios()
    return useQuery(
        [PERSON_KEY, personId, enabled],
        () =>
            callApi({
                method: 'GET',
                url: `/api/people?personId=${personId}`,
            }).then((data) => data.data),
        {
            staleTime: Infinity,
            enabled: enabled,
        },
    )
}

export const useResponsibleParty = (resourceId, enabled) => {
    const callApi = useAxios()
    return useQuery(
        ['RESPONSIBLE_PARTY', resourceId],
        () =>
            callApi({
                method: 'GET',
                url: `/api/people/responsibleParty?resourceId=${resourceId}`,
            }).then((data) => data.data),
        {
            enabled: enabled,
        },
    )
}

export const useLinkedPeople = (id) => {
    const callApi = useAxios()
    return useQuery(
        [`${CURRENT_PERSON_KEY}_LINKEDPEOPLE`, id],
        () =>
            callApi({
                method: 'GET',
                url: `/api/people/linkedPeople/?targetpersonId=${id}`,
            }).then((data) => data.data),
        {
            staleTime: Infinity,
        },
    )
}

export const useUpdatePersonLocale = () => {
    const callApi = useAxios()
    return useMutation(
        (locale) =>
            callApi({
                method: 'PATCH',
                url: '/api/people/locale',
                data: locale,
            }),
        {
            onSuccess: () => {
                window.location.reload()
            },
        },
    )
}

export const useValidateMasterPassword = (password) => {
    const callApi = useAxios()
    return useQuery(
        [PERSON_KEY, password],
        () =>
            callApi({
                method: 'POST',
                url: `/api/credentials/validateMasterPassword`,
                data: `"${password}"`,
            }).then((data) => data.data),
        {
            staleTime: Infinity,
            enabled: Boolean(password),
        },
    )
}

export const useMasterPasswordPublicKey = () => {
    const callApi = useAxios()
    return useQuery([MASTER_PASSWORD_PUBLIC_KEY], async () => {
        try {
            const response = await callApi({
                method: 'GET',
                url: `/api/Application/GetITShopConfigSetting?name=${MASTER_PASSWORD_PUBLIC_KEY}`,
            })
            return response.data
        } catch (error) {
            console.log('Error:', error)
        }
    })
}

export const useResourceApprovers = (resourceId) => {
    const callApi = useAxios()
    return useQuery(
        ['APPROVERS', resourceId],
        () =>
            callApi({
                method: 'GET',
                url: `api/people/resourceApprovers?resourceId=${resourceId}`,
            }).then((data) => data.data),
        {
            enabled: Boolean(resourceId),
        },
    )
}

export const useAssociatedLogins = () => {
    const callApi = useAxios()
    const [{ currentUserId }] = useAuthState()
    return useQuery(
        ['ASSOCIATED_LOGINS', currentUserId],
        () =>
            callApi({
                method: 'GET',
                url: `/api/people/AssociatedLogins?skip=0&take=1`,
            }).then((data) => data.data),
        {
            staleTime: Infinity,
            enabled: Boolean(currentUserId),
        },
    )
}

export const useGetBasePersonInfo = () => {
    const callApi = useAxios()
    return useQuery(
        ['BASE_PERSON_INFO'],
        () =>
            callApi({
                baseURL: appConfig.BASE_EID_URL,
                method: 'GET',
                url: '/ui/Common/Person/GetBaseUser',
            }).then((data) => data.data),
        {
            staleTime: Infinity,
        },
    )
}

export const useUserInfo = () => {
    const userManager = useUserManager()
    return useQuery(
        [USER_INFO_KEY],
        async () => {
            const user = await userManager.getUser()
            const rawResponse = await fetch(`${appConfig.API_URL}/api/user`, {
                headers: {
                    Authorization: `Bearer ${user?.access_token}`,
                },
            })
            const response = await rawResponse.json()
            return response
        },
        {
            staleTime: Infinity,
        },
    )
}
